var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faUsers } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Dialog, Link, Stack, TextField, Typography, styled } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { OrgType } from '../../enum';
import { useUserSessionAPI } from '../app/selectors';
import { Autocomplete, ErrorMessageBox, InputLabel } from '../common';
import { useDialog } from '../hooks';
import { SUPPORT_EMAIL } from '../layout/footer';
import { PageBody, SubTitle } from '../layout/styled';
import { BillingConfigDialog } from './billingConfigDialog';
import { BillingLogDialog } from './billingLog';
import { ChangeStatusDialog } from './changeStatus';
import { CompanyDialog } from './companyDialog';
import { DeleteUserDialog } from './deleteUser';
import { ExportLogDialog } from './exportLog';
import { ResetPasswordDialog } from './resetPassword';
import { useIsSuperAdmin, useOrgStatusAPI } from './selectors';
import { DialogMode } from './types';
import { UserDialog } from './userDialog';
import { UserTable } from './userTable';
export var StyledDialog = styled(Dialog)(function () {
  return {
    '& .MuiDialog-container': {
      '& .MuiPaper-root': {
        maxWidth: '50rem'
      }
    }
  };
});
var GroupButton = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(4)
  };
});
var LicenseInfoSubtitle = function () {
  var _a, _b;
  var t = useTranslation(['manageUser', 'translation']).t;
  var userSession = useUserSessionAPI()[0].data;
  var org = userSession === null || userSession === void 0 ? void 0 : userSession.orgs[0];
  var _c = useOrgStatusAPI(org === null || org === void 0 ? void 0 : org.id),
    orgStatusData = _c[0].data,
    fetchOrgStatus = _c[1],
    clearOrgStatus = _c[2];
  useEffect(function () {
    fetchOrgStatus();
    return function () {
      clearOrgStatus();
    };
  }, [clearOrgStatus, fetchOrgStatus, org]);
  var endDate = moment(org === null || org === void 0 ? void 0 : org.subscriptionEndDate).format(t('translation:dateFormat.shortDateFormat'));
  var total = (_a = org === null || org === void 0 ? void 0 : org.numberOfLicenses) !== null && _a !== void 0 ? _a : 0;
  var count = total - ((_b = orgStatusData === null || orgStatusData === void 0 ? void 0 : orgStatusData.usedLicenses) !== null && _b !== void 0 ? _b : 0);
  var licenseCountInfo = (org === null || org === void 0 ? void 0 : org.licensesUnlimited) ? t('licenseCountInfoUnlimited') : t('licenseCountInfo', {
    count: count,
    total: total
  });
  return _jsx(SubTitle, __assign({
    "data-testid": "subtitle-license-info"
  }, {
    children: _jsx(Trans, {
      i18nKey: "subtitleLicenseInfo",
      t: t,
      values: {
        licenseCountInfo: licenseCountInfo,
        endDate: endDate,
        email: SUPPORT_EMAIL
      },
      components: {
        b: _jsx("strong", {}),
        a: _jsx(Link, {
          href: "mailto:".concat(SUPPORT_EMAIL)
        })
      }
    })
  }));
};
var orgTypes = [OrgType.Cinareo, OrgType.Genesys];
var SUBSCRIPTION_WARNING_DAYS = 60;
export var ManageUsers = function () {
  var t = useTranslation('manageUser').t;
  var _a = useDialog(),
    dialogMode = _a.dialogMode,
    selectedUser = _a.data,
    handleOpenDialog = _a.handleOpenDialog,
    handleCloseDialog = _a.handleCloseDialog;
  var _b = useState(OrgType.Cinareo),
    selectedOrgType = _b[0],
    setSelectedOrgType = _b[1];
  var _c = useState(''),
    searchQuery = _c[0],
    setSearchQuery = _c[1];
  var _d = useState(0),
    usersRefreshKey = _d[0],
    setUsersRefreshKey = _d[1];
  var handleUserSaved = function (_a) {
    var created = _a.created;
    setUsersRefreshKey(function (key) {
      return key + 1;
    });
    if (created) {
      setSelectedOrgType(OrgType.Cinareo);
    }
  };
  var isSuperAdmin = useIsSuperAdmin();
  var userSession = useUserSessionAPI()[0].data;
  var _e = useState(undefined),
    expireMessage = _e[0],
    setExpireMessage = _e[1];
  useEffect(function () {
    var getExpireMessage = function () {
      var _a;
      if (isSuperAdmin) return undefined;
      var subscriptionEnd = (_a = userSession === null || userSession === void 0 ? void 0 : userSession.orgs[0]) === null || _a === void 0 ? void 0 : _a.subscriptionEndDate;
      if (!subscriptionEnd) return undefined;
      var todayUtc = moment.utc().startOf('day');
      var endUtc = moment.utc(subscriptionEnd).startOf('day');
      var daysLeft = endUtc.diff(todayUtc, 'days');
      if (daysLeft > SUBSCRIPTION_WARNING_DAYS || daysLeft < 0) return undefined;
      return {
        message: t('subscriptionExpireMessage', {
          count: daysLeft
        })
      };
    };
    var message = getExpireMessage();
    setExpireMessage(message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return _jsxs(PageBody, {
    children: [_jsxs(Typography, __assign({
      variant: "h1",
      "data-testid": "main-title"
    }, {
      children: [_jsx(FontAwesomeIcon, {
        icon: faUsers
      }), "\u00A0\u00A0", t('mainTitle')]
    })), _jsx(ErrorMessageBox, {
      errors: [expireMessage]
    }), isSuperAdmin ? _jsx(SubTitle, {
      children: t('subtitle')
    }) : _jsx(LicenseInfoSubtitle, {}), _jsxs(GroupButton, {
      children: [_jsxs(Button, __assign({
        variant: "contained",
        onClick: handleOpenDialog.bind(null, DialogMode.User, undefined),
        "data-testid": "add-user"
      }, {
        children: ["+\u00A0\u00A0", t('addNewUser')]
      })), isSuperAdmin && _jsxs(_Fragment, {
        children: [_jsxs(Button, __assign({
          variant: "contained",
          onClick: handleOpenDialog.bind(null, DialogMode.Company, undefined),
          "data-testid": "add-company"
        }, {
          children: ["+\u00A0\u00A0", t('addNewCompany')]
        })), _jsxs(Stack, __assign({
          direction: "row",
          spacing: 2,
          alignItems: "center",
          ml: "auto"
        }, {
          children: [_jsx(InputLabel, {
            children: t('platform')
          }), _jsx(Autocomplete, {
            sx: {
              width: '15rem'
            },
            options: orgTypes,
            value: selectedOrgType,
            onChange: setSelectedOrgType
          })]
        })), _jsx(Button, __assign({
          variant: "outlined",
          sx: {
            textTransform: 'uppercase'
          },
          onClick: handleOpenDialog.bind(null, DialogMode.BillingLog, undefined)
        }, {
          children: t('billingLog.title')
        })), _jsx(Button, __assign({
          variant: "outlined",
          sx: {
            textTransform: 'uppercase'
          },
          onClick: handleOpenDialog.bind(null, DialogMode.ExportLog, undefined)
        }, {
          children: t('exportLog.title')
        }))]
      })]
    }), _jsx(Box, __assign({
      sx: {
        mt: 3,
        mb: 2
      }
    }, {
      children: _jsx(TextField, {
        fullWidth: true,
        placeholder: t('searchPlaceholder'),
        value: searchQuery,
        onChange: function (e) {
          return setSearchQuery(e.target.value);
        },
        variant: "outlined",
        size: "small",
        "data-testid": "search-users"
      })
    })), _jsx(UserTable, {
      handleOpenDialog: handleOpenDialog,
      platform: selectedOrgType,
      searchQuery: searchQuery,
      refreshKey: usersRefreshKey
    }), _jsx(UserDialog, {
      open: dialogMode === DialogMode.User,
      user: selectedUser,
      onClose: handleCloseDialog,
      onUserSaved: handleUserSaved
    }), _jsx(CompanyDialog, {
      open: dialogMode === DialogMode.Company,
      onClose: handleCloseDialog,
      user: selectedUser,
      onUserSaved: handleUserSaved
    }), _jsx(ResetPasswordDialog, {
      open: dialogMode === DialogMode.ResetPassword,
      user: selectedUser,
      onClose: handleCloseDialog,
      onUserSaved: handleUserSaved
    }), _jsx(ChangeStatusDialog, {
      open: dialogMode === DialogMode.ChangeStatus,
      user: selectedUser,
      onClose: handleCloseDialog,
      onUserSaved: handleUserSaved
    }), _jsx(BillingLogDialog, {
      open: dialogMode === DialogMode.BillingLog,
      onClose: handleCloseDialog
    }), _jsx(BillingConfigDialog, {
      open: dialogMode === DialogMode.BillingConfig,
      onClose: handleCloseDialog,
      user: selectedUser
    }), _jsx(ExportLogDialog, {
      open: dialogMode === DialogMode.ExportLog,
      onClose: handleCloseDialog
    }), _jsx(DeleteUserDialog, {
      open: dialogMode === DialogMode.DeleteUser,
      user: selectedUser,
      onClose: handleCloseDialog,
      onUserSaved: handleUserSaved
    })]
  });
};